/**
 * Старый shell после деплоя: index/main в кэше ссылается на chunk с другим хэшем →
 * браузер даёт TypeError "Failed to fetch dynamically imported module" (esbuild/Vite)
 * или старый текст Webpack "Loading chunk N failed".
 *
 * Стратегия (production + development-стенды, не localhost/ng serve):
 * 1) сразу cache-bust document через ?_chunkRecover= на последний рабочий URL
 *    (не на failed navigation target — иначе белый экран)
 * 2) параметр снимаем из адресной строки уже на старте бутстрапа, чтобы он не
 *    «залипал» в url и не тащился с ручными перезагрузками вкладки
 * 3) число автоперезагрузок ограничено MAX_RECOVERY_ATTEMPTS (защита от бесконечного
 *    цикла перезагрузок); счётчик живёт в sessionStorage и переживает reload
 * 4) когда попытки исчерпаны — стоп: один report в Sentry, дальше silent
 */
import * as Sentry from '@sentry/angular';
import {environment} from '../../environments/environment';

const CHUNK_RECOVER_PARAM = '_chunkRecover';
const LAST_CHUNK_FAIL_TS_KEY = 'portal_chunk_last_fail_ts';
/** Сколько автоперезагрузок подряд уже сделали в рамках одной «серии» падений. */
const ATTEMPTS_KEY = 'portal_chunk_attempts';
/** Последний успешный router.url — куда возвращать после recovery. */
const LAST_GOOD_URL_KEY = 'portal_chunk_last_good_url';
/** Куда вернуть после bust (фиксируется в момент ошибки). */
const RETURN_URL_KEY = 'portal_chunk_return_url';
/** После успешной навигации чистим флаги только если с последнего fail прошло достаточно времени. */
const CLEAR_AFTER_HEALTHY_MS = 20000;
/** Максимум автоматических перезагрузок подряд — жёсткая защита от бесконечного цикла. */
const MAX_RECOVERY_ATTEMPTS = 2;
/** Если с последнего fail прошло больше — считаем это новой серией (напр. следующий деплой). */
const BURST_RESET_MS = 60000;

const LEGACY_KEYS = [
    'portal_chunk_reload_once',
    'portal_chunk_recovery_attempts',
    'portal_chunk_bust_done',
] as const;

let recoveryInFlight = false;
let exhaustedReported = false;
let returnRestoreDone = false;

export type StaleChunkHandleOptions = {
    /** Явный URL «до ошибки» (обычно router.url при NavigationError). */
    returnUrl?: string;
};

function isLikelyLocalDevHost(): boolean {
    try {
        const h = window.location.hostname;
        return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
    } catch {
        return false;
    }
}

export function isChunkRecoveryEnabled(): boolean {
    if (typeof window === 'undefined' || isLikelyLocalDevHost()) {
        return false;
    }
    return !!(environment.production || environment.development);
}

function messageFromLeaf(error: unknown): string {
    if (error == null) {
        return '';
    }
    if (typeof error === 'string') {
        return error;
    }
    if (error instanceof Error) {
        return error.message || '';
    }
    if (typeof error === 'object') {
        const o = error as Record<string, unknown>;
        if (typeof o.message === 'string') {
            return o.message;
        }
    }
    try {
        return String(error);
    } catch {
        return '';
    }
}

function collectErrorText(error: unknown, depth = 0, seen = new Set<unknown>()): string {
    if (error == null || depth > 8) {
        return '';
    }
    if (typeof error === 'object' || typeof error === 'function') {
        if (seen.has(error)) {
            return '';
        }
        seen.add(error);
    }

    const parts: string[] = [];
    const chunk = messageFromLeaf(error);
    if (chunk) {
        parts.push(chunk);
    }

    if (error instanceof Error) {
        const c = (error as Error & {cause?: unknown}).cause;
        if (c) {
            parts.push(collectErrorText(c, depth + 1, seen));
        }
    }

    if (typeof error === 'object' && error !== null) {
        const o = error as Record<string, unknown>;
        const nested = [o.rejection, o.reason, o.error, o.originalError, o.ngOriginalError];
        for (const n of nested) {
            if (n !== undefined && n !== error) {
                parts.push(collectErrorText(n, depth + 1, seen));
            }
        }
    }

    return parts.filter(Boolean).join(' ');
}

export function isStaleChunkLoadError(error: unknown): boolean {
    const msg = collectErrorText(error);
    if (!msg) {
        return false;
    }
    return (
        /Loading chunk \d+ failed/i.test(msg) ||
        /Failed to fetch dynamically imported module/i.test(msg) ||
        /Importing a module script failed/i.test(msg) ||
        /error loading dynamically imported module/i.test(msg)
    );
}

/** path + search + hash без _chunkRecover; только same-origin. */
function normalizeAppUrl(url: string): string {
    try {
        const u = new URL(url, window.location.origin);
        if (u.origin !== window.location.origin) {
            return '/';
        }
        u.searchParams.delete(CHUNK_RECOVER_PARAM);
        const qs = u.searchParams.toString();
        const path = u.pathname + (qs ? '?' + qs : '') + u.hash;
        return path || '/';
    } catch {
        return '/';
    }
}

function currentBrowserAppUrl(): string {
    return normalizeAppUrl(window.location.pathname + window.location.search + window.location.hash);
}

function stripChunkRecoverParam(): void {
    const u = new URL(window.location.href);
    if (!u.searchParams.has(CHUNK_RECOVER_PARAM)) {
        return;
    }
    u.searchParams.delete(CHUNK_RECOVER_PARAM);
    const qs = u.searchParams.toString();
    const clean = u.pathname + (qs ? '?' + qs : '') + u.hash;
    history.replaceState(history.state, '', clean);
}

function clearLegacyKeys(): void {
    for (const key of LEGACY_KEYS) {
        sessionStorage?.removeItem(key);
    }
}

function readAttempts(): number {
    try {
        return parseInt(sessionStorage?.getItem(ATTEMPTS_KEY) || '0', 10) || 0;
    } catch {
        return 0;
    }
}

function writeAttempts(value: number): void {
    try {
        sessionStorage?.setItem(ATTEMPTS_KEY, String(value));
    } catch {
        /* ignore */
    }
}

function readLastFailTs(): number {
    try {
        return parseInt(sessionStorage?.getItem(LAST_CHUNK_FAIL_TS_KEY) || '0', 10) || 0;
    } catch {
        return 0;
    }
}

/** Полный сброс состояния recovery (после «здоровой» сессии или по явному действию юзера). */
function resetRecoveryState(): void {
    try {
        sessionStorage?.removeItem(ATTEMPTS_KEY);
        sessionStorage?.removeItem(LAST_CHUNK_FAIL_TS_KEY);
        sessionStorage?.removeItem(RETURN_URL_KEY);
        clearLegacyKeys();
    } catch {
        /* ignore */
    }
    
    recoveryInFlight = false;
    exhaustedReported = false;
    returnRestoreDone = false;
}

/** Запоминаем последний успешный маршрут (вызывать на NavigationEnd). */
export function rememberChunkRecoveryGoodUrl(routerUrl: string): void {
    if (!isChunkRecoveryEnabled() || typeof sessionStorage === 'undefined') {
        return;
    }
    try {
        const clean = normalizeAppUrl(routerUrl);
        if (clean) {
            sessionStorage.setItem(LAST_GOOD_URL_KEY, clean);
        }
    } catch {
        /* ignore */
    }
}

function resolveReturnUrl(explicit?: string): string {
    if (explicit) {
        return normalizeAppUrl(explicit);
    }
    try {
        const lastGood = sessionStorage?.getItem(LAST_GOOD_URL_KEY);
        if (lastGood) {
            return normalizeAppUrl(lastGood);
        }
    } catch {
        /* ignore */
    }
    return currentBrowserAppUrl();
}

/** Bust на returnUrl (не на failed target в address bar). */
function replaceWithCacheBustParam(returnUrl: string): void {
    const safeReturn = normalizeAppUrl(returnUrl) || '/';
    try {
        sessionStorage?.setItem(RETURN_URL_KEY, safeReturn);
    } catch {
        /* ignore */
    }

    const u = new URL(safeReturn, window.location.origin);
    u.searchParams.set(CHUNK_RECOVER_PARAM, String(Date.now()));
    const target = u.toString();

    let navigated = false;
    const go = (): void => {
        if (navigated) {
            return;
        }
        navigated = true;
        window.location.replace(target);
    };

    if (typeof caches === 'undefined') {
        go();
        return;
    }

    const fallbackTimer = window.setTimeout(go, 400);
    caches
        .keys()
        .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
        .catch(() => undefined)
        .finally(() => {
            window.clearTimeout(fallbackTimer);
            go();
        });
}

function reportExhaustedOnce(error: unknown): void {
    if (exhaustedReported) {
        return;
    }
    exhaustedReported = true;
    try {
        Sentry.captureException(error, {
            tags: {chunk_recovery: 'exhausted'},
            level: 'warning',
        });
    } catch {
        /* ignore */
    }
    try {
        console.warn(
            '[chunk-recovery] Не удалось подтянуть свежий бандл после деплоя. ' +
                'Обновите страницу с очисткой кэша (Ctrl+Shift+R / ⌘+Shift+R).',
            error,
        );
    } catch {
        /* ignore */
    }
}

export function prepareChunkRecoveryForBootstrap(): void {
    if (typeof window === 'undefined') {
        return;
    }

    if (isLikelyLocalDevHost()) {
        // На локалке recovery выключен — полностью чистим любые следы.
        try {
            stripChunkRecoverParam();
            resetRecoveryState();
            sessionStorage?.removeItem(LAST_GOOD_URL_KEY);
        } catch {
            /* ignore */
        }
        return;
    }

    // На стендах/проде убираем служебный ?_chunkRecover из адресной строки сразу,
    // чтобы он не «залипал» в url и не тащился с ручными перезагрузками вкладки.
    // Счётчик попыток и returnUrl в sessionStorage НЕ трогаем — на них держится
    // и восстановление returnUrl, и защита от бесконечного цикла перезагрузок.
    try {
        stripChunkRecoverParam();
    } catch {
        /* ignore */
    }
}

/**
 * @returns true если caller не должен логировать ошибку дальше
 */
export function handleStaleChunkLoadError(
    error: unknown,
    options?: StaleChunkHandleOptions,
): boolean {
    if (!isChunkRecoveryEnabled() || !isStaleChunkLoadError(error)) {
        return false;
    }
    if (recoveryInFlight) {
        return true;
    }

    // Новая «серия», если с прошлого падения прошло много времени (например, новый деплой).
    const lastFail = readLastFailTs();
    const isNewBurst = lastFail === 0 || Date.now() - lastFail > BURST_RESET_MS;
    const attempts = isNewBurst ? 0 : readAttempts();

    if (attempts >= MAX_RECOVERY_ATTEMPTS) {
        // Лимит исчерпан — больше НЕ перезагружаемся сами (иначе бесконечный цикл).
        // Один report в Sentry, дальше silent.
        reportExhaustedOnce(error);
        return true;
    }

    try {
        const returnUrl = resolveReturnUrl(options?.returnUrl);
        writeAttempts(attempts + 1);
        try {
            sessionStorage?.setItem(LAST_CHUNK_FAIL_TS_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }
        recoveryInFlight = true;
        replaceWithCacheBustParam(returnUrl);
        return true;
    } catch {
        recoveryInFlight = false;
        return false;
    }
}

/**
 * После успешной навигации: снять ?_chunkRecover и вернуть URL до ошибки, если нужно.
 * @returns URL для router.navigateByUrl(..., {replaceUrl: true}) или null
 */
export function consumeChunkRecoveryReturnUrl(): string | null {
    if (returnRestoreDone) {
        return null;
    }
    try {
        const hasRecoverParam = new URL(window.location.href).searchParams.has(CHUNK_RECOVER_PARAM);
        const storedReturn = sessionStorage?.getItem(RETURN_URL_KEY);
        if (!hasRecoverParam && !storedReturn) {
            return null;
        }

        returnRestoreDone = true;
        stripChunkRecoverParam();

        if (!storedReturn) {
            return null;
        }
        sessionStorage.removeItem(RETURN_URL_KEY);

        const target = normalizeAppUrl(storedReturn);
        const current = currentBrowserAppUrl();
        if (!target || target === current) {
            return null;
        }
        return target;
    } catch {
        return null;
    }
}

/**
 * После успешной навигации: убираем служебное и при «здоровой» сессии сбрасываем флаги.
 */
export function clearChunkReloadSessionFlag(): void {
    try {
        // На случай если consume ещё не вызывали
        stripChunkRecoverParam();

        const lastFail = readLastFailTs();
        const healthyLongEnough = lastFail === 0 || Date.now() - lastFail >= CLEAR_AFTER_HEALTHY_MS;
        if (!healthyLongEnough) {
            return;
        }

        resetRecoveryState();
    } catch {
        /* ignore */
    }
}
